// SaveQueue -- framework-free, per-record autosave write queue.
//
// Every edit surface in the app persists changes through this queue instead of
// an explicit Save button. The queue guarantees, per record key:
//
//   * at most ONE mutation in flight at a time, and
//   * while one is in flight, newer payloads coalesce into a single "next"
//     job (latest wins), which runs after the in-flight one settles.
//
// That serialization is the correctness core of autosave: without it a slow,
// older request can land after a newer, faster one and silently revert data.
//
// Keys follow a `model:id` convention (e.g. `task:${id}`, `item:${id}`) so
// two components editing the same record share one lane.
//
// Coalescing is only free when every payload on a lane is interchangeable --
// which is true of useAutosave's, since each is built from the record's
// newest state at the moment it runs. A hand-built job carrying its own
// distinct write (Trip.jsx's import-replace) is NOT interchangeable: if a
// newer payload supersedes it, it silently never happens. Such jobs pass an
// `onDropped(reason)` callback so they can unwind and tell the user.
//
// Feedback: subscribers (SaveStatusPill, useAutosave) observe an aggregate
// snapshot -- 'error' > 'saving' > 'saved' > 'idle' -- with anti-flicker
// baked in: records still in their debounce window report nothing, "saving"
// holds for a minimum display time, and "saved" lingers briefly before
// clearing. Failed saves keep their job so Retry can re-run it.

import { extractErrorText } from '../fleet-shared/Common.mjs';

export const DEFAULT_DEBOUNCE_MS = 1500;
const RETRY_DELAYS_MS = [1000, 3000];
const MIN_SAVING_DISPLAY_MS = 400;
const SAVED_DISPLAY_MS = 2000;

// Only network-ish / server-side failures are worth retrying automatically.
// Validation, auth, and conditional-write failures will fail the same way
// again, so they surface immediately.
export function isRetryableError(err) {
  if (!err) return false;
  const name = err.name || '';
  const message = String(err.message || '');
  if (name === 'NetworkError' || /network|failed to fetch|timed? ?out/i.test(message)) {
    return true;
  }
  // Amplify GraphQL errors arrive as { errors: [{ errorType, message }] }.
  const gqlErrors = Array.isArray(err.errors) ? err.errors : [];
  return gqlErrors.some((e) => {
    const type = String(e?.errorType || '');
    if (/Internal|Service|Timeout|Unavailable|Throttl/i.test(type)) return true;
    return /network/i.test(String(e?.message || ''));
  });
}

export function createSaveQueue() {
  // key -> record. A record exists while there is anything to show or do for
  // that key (pending job, in-flight save, saved latch, or error).
  //   { phase, job, timer, inFlightJob, nextJob, attempt, suppressedJob,
  //     errorMessage }
  // phase: 'debouncing' | 'inflight' | 'retrywait' | 'saved' | 'error' |
  //        'cancelled' (cancel() left a suppressed save still in flight)
  const records = new Map();
  const listeners = new Set();

  // Minimum-display latch for "Saving..." so a fast save doesn't strobe.
  let savingHold = false;
  let savingHoldTimer = null;

  let snapshot = { status: 'idle', errorMessage: null, errorKey: null };

  function notify() {
    for (const listener of [...listeners]) {
      listener();
    }
  }

  // A job that will never run -- superseded by a newer payload on the same
  // lane, or dropped by cancel()/cancelPending(). Callers that hand-build
  // jobs (rather than going through useAutosave, whose payloads are all
  // interchangeable) use this to unwind whatever UI state they armed when
  // they scheduled it. `reason` is 'superseded' or 'cancelled'.
  function drop(job, reason) {
    if (!job || !job.onDropped) return;
    try {
      job.onDropped(reason);
    } catch (err) {
      console.error('SaveQueue onDropped callback failed', err);
    }
  }

  function computePendingCount() {
    let count = 0;
    for (const rec of records.values()) {
      // A cancelled-but-still-in-flight save is nobody's pending work: its
      // result is discarded whether it lands or not.
      const inFlight = rec.inFlightJob && rec.inFlightJob !== rec.suppressedJob;
      if (rec.job || inFlight || rec.nextJob) count += 1;
    }
    return count;
  }

  function computeStatus() {
    let anyError = null;
    let anyErrorKey = null;
    let anySaving = false;
    let anySaved = false;
    for (const [key, rec] of records.entries()) {
      if (rec.phase === 'cancelled') continue;
      if (rec.phase === 'error') {
        if (!anyError) {
          anyError = rec;
          anyErrorKey = key;
        }
      } else if (rec.phase === 'inflight' || rec.phase === 'retrywait') anySaving = true;
      else if (rec.phase === 'saved') anySaved = true;
    }
    if (anyError) {
      return { status: 'error', errorMessage: anyError.errorMessage, errorKey: anyErrorKey };
    }
    if (anySaving || savingHold) return { status: 'saving', errorMessage: null, errorKey: null };
    if (anySaved) return { status: 'saved', errorMessage: null, errorKey: null };
    return { status: 'idle', errorMessage: null, errorKey: null };
  }

  // Recompute the aggregate snapshot and tell subscribers something changed.
  // Always notifies (per-record status may change without moving the
  // aggregate), but keeps the snapshot object stable unless its contents
  // changed so useSyncExternalStore consumers of getSnapshot don't re-render.
  function update() {
    const next = computeStatus();
    if (
      next.status !== snapshot.status ||
      next.errorMessage !== snapshot.errorMessage ||
      next.errorKey !== snapshot.errorKey
    ) {
      snapshot = next;
    }
    notify();
  }

  function beginSavingHold() {
    savingHold = true;
    if (savingHoldTimer) clearTimeout(savingHoldTimer);
    savingHoldTimer = setTimeout(() => {
      savingHoldTimer = null;
      savingHold = false;
      update();
    }, MIN_SAVING_DISPLAY_MS);
  }

  function clearRecordTimer(rec) {
    if (rec.timer) {
      clearTimeout(rec.timer);
      rec.timer = null;
    }
  }

  function removeRecord(key, rec) {
    clearRecordTimer(rec);
    records.delete(key);
  }

  function ensureRecord(key) {
    let rec = records.get(key);
    if (!rec) {
      rec = {
        phase: 'saved', // placeholder; set by caller before update()
        job: null,
        timer: null,
        inFlightJob: null,
        nextJob: null,
        attempt: 0,
        suppressedJob: null,
        errorMessage: null,
      };
      records.set(key, rec);
    }
    return rec;
  }

  function startRun(key, rec) {
    const job = rec.job;
    rec.job = null;
    clearRecordTimer(rec);
    rec.inFlightJob = job;
    rec.phase = 'inflight';
    beginSavingHold();
    update();

    Promise.resolve()
      .then(() => job.run())
      .then(
        (saved) => settleSuccess(key, rec, job, saved),
        (err) => settleFailure(key, rec, job, err)
      );
  }

  // Promote a coalesced payload now that the lane has freed up. Its debounce
  // is measured from when it was SCHEDULED, not from when the older save
  // settled -- otherwise an edit typed during a slow round trip would wait a
  // full extra debounce on top of it, and "saves ~1.5s after the last
  // keystroke" would stop being true under latency.
  function startNext(key, rec) {
    if (!rec.nextJob) return false;
    const { job: pending, delayMs, scheduledAt } = rec.nextJob;
    rec.nextJob = null;
    rec.job = pending;
    const remaining = delayMs > 0 ? Math.max(0, delayMs - (Date.now() - scheduledAt)) : 0;
    if (remaining > 0) {
      rec.phase = 'debouncing';
      rec.timer = setTimeout(() => startRun(key, rec), remaining);
      update();
    } else {
      startRun(key, rec);
    }
    return true;
  }

  // A cancel() (or reset()) landed while this job was in flight: discard the
  // result silently -- no callbacks, no status. Suppression is tracked on the
  // JOB, not on the record, so a schedule() that re-arms this lane in the
  // meantime can't un-cancel it. For the same reason this never deletes by
  // key without checking identity: the key may already belong to a fresh
  // record that this settlement has no business disturbing.
  function settleSuppressed(key, rec, job) {
    rec.inFlightJob = null;
    rec.suppressedJob = null;
    if (startNext(key, rec)) return;
    if (records.get(key) === rec) removeRecord(key, rec);
    update();
  }

  function settleSuccess(key, rec, job, saved) {
    if (rec.suppressedJob === job) {
      settleSuppressed(key, rec, job);
      return;
    }
    rec.inFlightJob = null;
    rec.attempt = 0;
    rec.errorMessage = null;
    if (job.onSaved) {
      try {
        job.onSaved(saved);
      } catch (err) {
        // A consumer's post-save bookkeeping must not poison the queue.
        console.error('SaveQueue onSaved callback failed', err);
      }
    }
    if (startNext(key, rec)) return;
    rec.phase = 'saved';
    rec.timer = setTimeout(() => {
      rec.timer = null;
      // Identity-checked like settleSuppressed: never evict a record this
      // timer doesn't own.
      if (
        records.get(key) === rec &&
        rec.phase === 'saved' &&
        !rec.job &&
        !rec.inFlightJob &&
        !rec.nextJob
      ) {
        records.delete(key);
        update();
      }
    }, SAVED_DISPLAY_MS);
    update();
  }

  function settleFailure(key, rec, job, err) {
    if (rec.suppressedJob === job) {
      settleSuppressed(key, rec, job);
      return;
    }
    rec.inFlightJob = null;
    // A newer payload supersedes the failed one -- run it promptly instead of
    // retrying stale data. The failed job is done either way: it gets
    // onDropped, not onError.
    if (rec.nextJob) {
      const { job: pending } = rec.nextJob;
      rec.nextJob = null;
      rec.job = pending;
      rec.attempt = 0;
      drop(job, 'superseded');
      startRun(key, rec);
      return;
    }
    if (isRetryableError(err) && rec.attempt < RETRY_DELAYS_MS.length) {
      const delay = RETRY_DELAYS_MS[rec.attempt];
      rec.attempt += 1;
      rec.job = job;
      rec.phase = 'retrywait';
      rec.timer = setTimeout(() => startRun(key, rec), delay);
      update();
      return;
    }
    rec.job = job; // kept for retry()
    rec.phase = 'error';
    rec.errorMessage = extractErrorText(err);
    if (job.onError) {
      try {
        job.onError(err);
      } catch (callbackErr) {
        console.error('SaveQueue onError callback failed', callbackErr);
      }
    }
    update();
  }

  const queue = {
    // job: { run: async () => saved, onSaved?, onError?, onDropped? }
    schedule(key, job, { delayMs = DEFAULT_DEBOUNCE_MS } = {}) {
      const rec = ensureRecord(key);
      rec.errorMessage = null;
      if (rec.inFlightJob) {
        // Coalesce: latest payload wins, so whatever was already waiting
        // never runs. That is free for interchangeable useAutosave payloads
        // (each is built from the newest state when it runs) but NOT for a
        // hand-built job carrying its own distinct write -- hence onDropped.
        if (rec.nextJob) drop(rec.nextJob.job, 'superseded');
        rec.nextJob = { job, delayMs, scheduledAt: Date.now() };
        update();
        return;
      }
      if (rec.job) drop(rec.job, 'superseded');
      rec.attempt = 0;
      rec.job = job;
      clearRecordTimer(rec);
      if (delayMs > 0) {
        rec.phase = 'debouncing';
        rec.timer = setTimeout(() => startRun(key, rec), delayMs);
        update();
      } else {
        startRun(key, rec);
      }
    },

    // Run a pending (debouncing or retry-waiting) job now. No-op for records
    // that are idle, already in flight, or parked in the error state.
    flush(key) {
      const rec = records.get(key);
      if (!rec || !rec.job || rec.inFlightJob) return;
      if (rec.phase === 'debouncing' || rec.phase === 'retrywait') {
        startRun(key, rec);
      }
    },

    flushAll() {
      for (const key of [...records.keys()]) {
        queue.flush(key);
      }
    },

    // Drop everything for a record, including suppressing the result of an
    // in-flight save. For delete flows: a late update against a deleted row
    // must neither error nor fire callbacks.
    cancel(key) {
      const rec = records.get(key);
      if (!rec) return;
      clearRecordTimer(rec);
      if (rec.job) drop(rec.job, 'cancelled');
      if (rec.nextJob) drop(rec.nextJob.job, 'cancelled');
      rec.job = null;
      rec.nextJob = null;
      rec.errorMessage = null;
      if (rec.inFlightJob) {
        // Marked on the job so a later schedule() on this lane can't
        // resurrect the settlement this cancel exists to swallow. The record
        // itself stays put so that later schedule() still serializes behind
        // the write that is genuinely still in flight.
        rec.suppressedJob = rec.inFlightJob;
        rec.phase = 'cancelled';
        drop(rec.inFlightJob, 'cancelled');
      } else {
        records.delete(key);
      }
      update();
    },

    // Drop only what hasn't started: the pending/error job and any coalesced
    // next payload. An in-flight save (already-valid data) is left to finish.
    // For validation: when the form goes invalid, a stale pre-invalid payload
    // must not fire behind the user's back.
    cancelPending(key) {
      const rec = records.get(key);
      if (!rec) return;
      clearRecordTimer(rec);
      if (rec.job) drop(rec.job, 'cancelled');
      if (rec.nextJob) drop(rec.nextJob.job, 'cancelled');
      rec.job = null;
      rec.nextJob = null;
      rec.errorMessage = null;
      if (rec.inFlightJob) {
        // Leave the in-flight save; phase stays 'inflight'.
        update();
        return;
      }
      records.delete(key);
      update();
    },

    retry(key) {
      const rec = records.get(key);
      if (!rec || rec.phase !== 'error' || !rec.job || rec.inFlightJob) return;
      rec.attempt = 0;
      startRun(key, rec);
    },

    retryAll() {
      for (const [key, rec] of [...records.entries()]) {
        if (rec.phase === 'error') queue.retry(key);
      }
    },

    // Per-record status for useAutosave: 'idle' | 'debouncing' | 'saving' |
    // 'saved' | 'error'.
    getRecordStatus(key) {
      const rec = records.get(key);
      if (!rec) return 'idle';
      switch (rec.phase) {
        case 'debouncing':
          return 'debouncing';
        case 'inflight':
        case 'retrywait':
          return 'saving';
        case 'saved':
          return 'saved';
        case 'error':
          return 'error';
        default:
          return 'idle';
      }
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    getSnapshot() {
      return snapshot;
    },

    // For the beforeunload guard: is anything not yet safely saved? Counts an
    // error-parked record, because its data genuinely isn't on the server --
    // that is the question `beforeunload` and the e2e drain are asking.
    pendingCount() {
      return computePendingCount();
    },

    // Is this lane still going to change the record ON ITS OWN -- a payload
    // debouncing, in flight, or coalesced behind one?
    //
    // Deliberately unlike pendingCount(): an error-parked lane is NOT pending
    // here. It won't progress without a Retry, and until then the server holds
    // exactly what every other reader already sees. A caller asking this is
    // asking "will waiting change what I read?", and for a failure the honest
    // answer is no.
    isPending(key) {
      const rec = records.get(key);
      if (!rec || rec.phase === 'error') return false;
      const inFlight = rec.inFlightJob && rec.inFlightJob !== rec.suppressedJob;
      return Boolean(rec.job || inFlight || rec.nextJob);
    },

    // Every lane isPending() is true for, right now.
    //
    // For a page that has to wait on writes it can't name: the places list
    // reloads the whole gazetteer after the edit page unmounted, and the edit
    // page's lane key is derived from a row id the list doesn't know it
    // touched. Filter these by prefix and hand them to settledAll.
    //
    // Same error-lane exclusion as isPending, and for the same reason: a
    // parked lane will never drain on its own, so waiting on it would hang.
    pendingKeys() {
      return [...records.keys()].filter((key) => queue.isPending(key));
    },

    // Resolve once isPending(key) is false.
    //
    // For pages that read a record right after someone else edited it: a
    // fetch issued while the write is still in flight can serve back the
    // pre-save row, and no later render fixes that. Await this first.
    //
    // Callers awaiting this from an effect should pass the `signal` of an
    // AbortController they abort on cleanup. A lane that never drains (a
    // retry chain the user leaves running, a page navigated away from) would
    // otherwise keep this subscription -- and everything the promise closes
    // over -- alive for the life of the page. Aborting RESOLVES rather than
    // rejects: every caller re-checks that it's still mounted on the far side
    // of the await anyway, and a rejection here would just be noise.
    settled(key, { signal } = {}) {
      if (!queue.isPending(key) || (signal && signal.aborted)) return Promise.resolve();
      return new Promise((resolve) => {
        let unsubscribe = null;
        const finish = () => {
          if (unsubscribe) unsubscribe();
          if (signal) signal.removeEventListener('abort', finish);
          resolve();
        };
        unsubscribe = queue.subscribe(() => {
          if (queue.isPending(key)) return;
          finish();
        });
        if (signal) signal.addEventListener('abort', finish, { once: true });
      });
    },

    settledAll(keys, options) {
      return Promise.all([...new Set(keys)].map((key) => queue.settled(key, options)));
    },

    // Tests only: module-level state survives Jest's resetMocks, so
    // setupTests.js calls this after each test.
    reset() {
      for (const rec of records.values()) {
        clearRecordTimer(rec);
        rec.job = null;
        rec.nextJob = null;
        rec.suppressedJob = rec.inFlightJob; // swallow late settlements
      }
      records.clear();
      if (savingHoldTimer) {
        clearTimeout(savingHoldTimer);
        savingHoldTimer = null;
      }
      savingHold = false;
      snapshot = { status: 'idle', errorMessage: null, errorKey: null };
      notify();
    },
  };

  return queue;
}

// The app-wide singleton. All edit surfaces share it so the global
// SaveStatusPill sees every save, including ones still finishing after the
// page that started them unmounted.
export const saveQueue = createSaveQueue();

// Best-effort flush when the tab is backgrounded or closing, and automatic
// retry when connectivity returns. Guarded so importing this module in
// non-browser contexts (or very early) is safe.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') saveQueue.flushAll();
  });
}
// Whether an e2e test hook may be exposed on this host.
//
// Kept off the production site. Gating on hostname rather than NODE_ENV
// deliberately: e2e runs against `npm start` by default but also against
// dev.taskpogo.com (a production build), and both need the hooks. Pointing
// e2e at www.taskpogo.com then fails loudly in the helper that wanted the
// hook, rather than quietly skipping the wait it was supposed to perform.
//
// Exported because the save queue is no longer the only thing an e2e spec has
// to wait on -- Trip.jsx exposes the sibling-date cascade the same way, and
// the two must agree on what "not production" means.
export function testHooksAllowed() {
  if (typeof window === 'undefined') return false;
  const host = (window.location && window.location.hostname) || '';
  return !/^(www\.)?taskpogo\.com$/i.test(host);
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => saveQueue.flushAll());
  window.addEventListener('online', () => saveQueue.retryAll());

  // Test hook: the e2e suite's awaitAutosaved() drains the queue through
  // this handle (flushAll + poll pendingCount) before reloading -- a "Saved"
  // pill sighting alone can't prove a newer, still-debouncing edit landed.
  if (testHooksAllowed()) {
    window.__taskPogoSaveQueue = saveQueue;
  }
}
